import { create } from "zustand";
import type { AuthClient, ClientFilter, ClientStats } from "./clients-types";
import * as api from "./clients-api";

interface ClientsState {
  clients: AuthClient[];
  total: number;
  stats: ClientStats | null;
  filter: ClientFilter;
  loading: boolean;
  selectedClient: AuthClient | null;
  modalBlock: boolean;
  modalDelete: boolean;

  fetchClients: () => Promise<void>;
  fetchStats: () => Promise<void>;
  setFilter: (filter: Partial<ClientFilter>) => void;
  setSelectedClient: (client: AuthClient | null) => void;
  setModalBlock: (open: boolean) => void;
  setModalDelete: (open: boolean) => void;
  blockClient: (uuid: string, reason: string) => Promise<void>;
  unblockClient: (uuid: string) => Promise<void>;
  deleteClient: (uuid: string, reason: string) => Promise<void>;
  restoreClient: (uuid: string) => Promise<void>;
}

export const useClientsStore = create<ClientsState>((set, get) => ({
  clients: [],
  total: 0,
  stats: null,
  filter: { query: "", status: "", page: 1, limit: 25, sort_by: "", sort_order: "desc" },
  loading: false,
  selectedClient: null,
  modalBlock: false,
  modalDelete: false,

  fetchClients: async () => {
    set({ loading: true });
    try {
      const res = await api.getClients(get().filter);
      set({ clients: res.clients || [], total: res.total, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  fetchStats: async () => {
    try {
      const stats = await api.getClientStats();
      set({ stats });
    } catch {
      // ignore
    }
  },

  setFilter: (partial) => {
    const filter = { ...get().filter, ...partial };
    set({ filter });
  },

  setSelectedClient: (client) => set({ selectedClient: client }),
  setModalBlock: (open) => set({ modalBlock: open }),
  setModalDelete: (open) => set({ modalDelete: open }),

  blockClient: async (uuid, reason) => {
    await api.blockClient(uuid, reason);
    set({ modalBlock: false, selectedClient: null });
    get().fetchClients();
    get().fetchStats();
  },

  unblockClient: async (uuid) => {
    await api.unblockClient(uuid);
    get().fetchClients();
    get().fetchStats();
  },

  deleteClient: async (uuid, reason) => {
    await api.deleteClient(uuid, reason);
    set({ modalDelete: false, selectedClient: null });
    get().fetchClients();
    get().fetchStats();
  },

  restoreClient: async (uuid) => {
    await api.restoreClient(uuid);
    get().fetchClients();
    get().fetchStats();
  },
}));
